/**
 * Charm Builder (v3) — "Kendi Takını Tasarla"
 * Standalone (theme.js'e dokunmaz). Bir PRODUCT sayfasında çalışır (base = sayfanın ürünü).
 *
 * - Renk (varyant) seçimi → sol görsel değişir
 * - Charm'lar sürükle-bırak veya tıkla ile zincirdeki POINT'lere yerleştirilir (her point ≤1 charm)
 * - Yerleşik charm'lar gerçek takı gibi asılı durur ve sallanır (drop-in / sway / leave)
 * - "Sepete Ekle": client-side COMPOSITE görsel üretilir → base satırına multipart yüklenir
 *   (admin siparişinde + sepette görünür). Alin fixed price akışında charm'lar ayrı sepet satırı olarak eklenmez.
 * - design_mode: görsele tıklayarak slot_positions üreten Point Editörü
 */
(function () {
  'use strict';

  var CHARM_FRAC = 0.14;       // charm genişliği = tuval genişliği × bu × scale
  var SNAP_RADIUS_PCT = 14;    // sürükle-bırak snap yarıçapı (% mesafe)
  var DRAG_THRESHOLD = 6;      // px — tık vs sürükleme ayrımı
  var COMPOSITE_SIZE = 1600;   // composite PNG kenar (px, kare)

  /* Shopify standart para formatlayıcı */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var formatString = format || '{{amount}}';
    var re = /\{\{\s*(\w+)\s*\}\}/;
    var match = formatString.match(re);
    function wd(n, p, t, d) {
      p = p == null ? 2 : p; t = t == null ? ',' : t; d = d == null ? '.' : d;
      if (isNaN(n) || n == null) return '0';
      n = (n / 100.0).toFixed(p);
      var parts = n.split('.'), dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + t);
      return dollars + (parts[1] ? d + parts[1] : '');
    }
    var token = match ? match[1] : 'amount', v;
    switch (token) {
      case 'amount': v = wd(cents, 2); break;
      case 'amount_no_decimals': v = wd(cents, 0); break;
      case 'amount_with_comma_separator': v = wd(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': v = wd(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': v = wd(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': v = wd(cents, 0, ' ', ''); break;
      case 'amount_with_apostrophe_separator': v = wd(cents, 2, "'", '.'); break;
      default: v = wd(cents, 2);
    }
    return match ? formatString.replace(re, v) : v;
  }

  // Shopify CDN görsel URL'sinde width'i değiştir (composite için ayrı/temiz CORS cache + yüksek çözünürlük)
  function setWidth(url, w) {
    if (!url) return url;
    if (/[?&]width=\d+/.test(url)) return url.replace(/([?&]width=)\d+/, '$1' + w);
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  function loadImage(url) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';   // src'den ÖNCE
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('img load: ' + url)); };
      img.src = url;
    });
  }

  class CharmBuilder {
    constructor(root) {
      if (!root || root.__charmBuilderInit) return;
      root.__charmBuilderInit = true;
      this.root = root;

      this.moneyFormat = (window.theme && window.theme.moneyFormat) || '{{amount}} TL';
      var base = (window.theme && window.theme.routes && window.theme.routes.cart && window.theme.routes.cart.base) || root.dataset.cartUrl || '/cart';
      this.urls = { base: base, cartJs: base + '.js', add: base + '/add', addJs: base + '/add.js' };
      this.designMode = root.dataset.designMode === 'true';
      // Editör: tema customizer'ında (designMode) VEYA canlı sayfada ?cb-points=1 ile
      this.editorMode = this.designMode || new URLSearchParams(location.search).has('cb-points');

      this.state = {
        baseVariantId: null,
        basePrice: 0,
        baseImage: '',
        placements: {},   // pointIndex -> { variantId, title, price, image, scale }
        armed: null,      // tıkla-yerleştir için seçili charm kartı
        builderId: null
      };
      this.placed = { byId: new Map() };  // 'p<idx>' -> wrapper el
      this._swaySeq = 0;

      this.cache();
      this.initBase();
      this.bind();
      this.render();

      if (this.editorMode) this.initEditor();
      this.loadCatalog();
    }

    cache() {
      var r = this.root;
      this.els = {
        canvas: r.querySelector('[data-cb-canvas]'),
        baseImage: r.querySelector('[data-cb-base-image]'),
        overlay: r.querySelector('[data-cb-overlay]'),
        pointsLayer: r.querySelector('[data-cb-points]'),
        slotsUsed: r.querySelector('[data-cb-slots-used]'),
        slotsTotal: r.querySelector('[data-cb-slots-total]'),
        total: r.querySelector('[data-cb-total]'),
        addBtn: r.querySelector('[data-cb-add]'),
        error: r.querySelector('[data-cb-error]'),
        summary: r.querySelector('[data-cb-summary]'),
        variantName: r.querySelector('[data-cb-variant-name]')
      };
      this.charmCards = Array.prototype.slice.call(r.querySelectorAll('[data-cb-charm]'));
      this.variantBtns = Array.prototype.slice.call(r.querySelectorAll('[data-cb-variant]'));
      this.filterBtns = Array.prototype.slice.call(r.querySelectorAll('[data-cb-filter]'));
      this.refreshPoints();
    }

    refreshPoints() {
      this.points = [];
      this.pointsByIndex = {};
    }

    initBase() {
      this.state.count = null;
      this.state.baseImage = this.els.baseImage ? (this.els.baseImage.getAttribute('src') || '') : '';
      var stored = JSON.parse(this.root.querySelector('[data-cb-positions]').textContent || 'null');
      if (typeof stored === 'string' && stored.trim()) {
        try { stored = JSON.parse(stored); } catch (e) { stored = null; }
      }
      this.layouts = Array.isArray(stored) ? { [stored.length]: stored } : (stored || {});
      this.countBtns = Array.from(this.root.querySelectorAll('[data-cb-count]'));
      var self = this;
      this.countBtns.forEach(function (btn) {
        btn.disabled = !self.editorMode && !self.variantBtns.some(function (v) {
          return self.variantCount(v) === Number(btn.dataset.cbCount) && !v.disabled;
        });
      });
      var available = this.variantBtns.filter(function (btn) { return !btn.disabled && self.variantCount(btn); });
      var initial = available.find(function (btn) {
        return btn.dataset.variantId === self.root.dataset.defaultVariantId;
      }) || available[0] || this.variantBtns.find(function (btn) { return self.variantCount(btn); });
      this.selectCount(initial ? this.variantCount(initial) : 3);
      if (initial && !initial.disabled) this.selectVariant(initial);
    }

    variantCount(btn) {
      var value = this.normalizeTag(btn.dataset.count).replace(/ı/g, 'i');
      if (/^tek(?:li)?(?:$|[\s’'’-])/.test(value)) return 1;
      var match = value.match(/^([1345])(?:$|[^0-9])/);
      return match ? Number(match[1]) : null;
    }

    selectCount(count) {
      if (this.submitting || [1, 3, 4, 5].indexOf(count) === -1 || this.state.count === count) return;
      this.state.count = count;
      this.state.baseVariantId = null;
      this.state.basePrice = 0;
      this.state.color = '';
      this.reset();
      this.countBtns.forEach(function (btn) {
        var selected = Number(btn.dataset.cbCount) === count;
        btn.classList.toggle('is-selected', selected);
        btn.setAttribute('aria-pressed', String(selected));
      });
      var self = this;
      this.variantBtns.forEach(function (btn) {
        btn.hidden = self.variantCount(btn) !== count;
        btn.classList.remove('is-selected');
        btn.setAttribute('aria-pressed', 'false');
      });
      if (this.els.variantName) this.els.variantName.textContent = '';
      var positions = this.layouts[count];
      var valid = Array.isArray(positions) && positions.length === count && positions.every(function (p) {
        return p && Number.isFinite(p.x) && Number.isFinite(p.y) && p.x >= 0 && p.x <= 100 && p.y >= 0 && p.y <= 100;
      });
      if (!valid) positions = [];
      this.els.pointsLayer.innerHTML = '';
      this.pointsByIndex = {};
      this.points = positions.map(function (p, i) {
        var el = document.createElement('button');
        el.type = 'button'; el.className = 'charm-builder__point';
        el.dataset.pointIndex = i;
        el.style.left = p.x + '%'; el.style.top = p.y + '%';
        el.setAttribute('aria-label', 'Charm alanı ' + (i + 1));
        el.innerHTML = '<span class="charm-builder__point-dot"></span>';
        self.els.pointsLayer.appendChild(el);
        return self.pointsByIndex[i] = { el: el, index: i, x: p.x, y: p.y };
      });
      if (this.editorLoad) this.editorLoad();
      var firstColor = this.variantBtns.find(function (btn) { return !btn.hidden && !btn.disabled; });
      if (firstColor) this.selectVariant(firstColor);
      this.clearError();
      this.render();
    }

    bind() {
      var self = this;

      this.countBtns.forEach(function (btn) {
        btn.addEventListener('click', function () { self.selectCount(Number(btn.dataset.cbCount)); });
      });
      this.filterBtns.forEach(function (btn) {
        btn.addEventListener('click', function () { self.selectFilter(btn.dataset.cbFilter || 'all'); });
      });
      // Renk seçimi
      this.variantBtns.forEach(function (btn) {
        btn.addEventListener('click', function () { self.selectVariant(btn); });
      });

      this.bindCharmCards(this.charmCards);

      this.els.pointsLayer.addEventListener('click', function (e) {
        var el = e.target.closest('[data-point-index]');
        if (!el || self.editorMode) return;
        var index = Number(el.dataset.pointIndex);
        if (self.state.armed) {
          self.placeCharmAtPoint(index, self.charmFromCard(self.state.armed));
          self.disarm();
        } else self.removeCharmAtPoint(index);
      });

      // Özet butonları
      if (this.els.addBtn) this.els.addBtn.addEventListener('click', function () { self.addToCart(); });
      var resetBtn = this.root.querySelector('[data-cb-reset]');
      if (resetBtn) resetBtn.addEventListener('click', function () { self.reset(); });

      if (this.els.baseImage) this.els.baseImage.addEventListener('load', function () {
        self.syncImageLayers();
        self.render();
      });
      window.addEventListener('resize', function () { self.syncImageLayers(); });
    }

    bindCharmCards(cards) {
      var self = this;
      // Yeni charmlar yalnızca + düğmesiyle eklenir; kart yüzeyi kaydırılabilir.
      cards.forEach(function (card) {
        if (card.dataset.cbBound) return;
        card.dataset.cbBound = 'true';
        // stepper butonları (pointerdown'a stopPropagation → kart drag/arm tetiklenmesin)
        Array.prototype.slice.call(card.querySelectorAll('[data-cb-step]')).forEach(function (btn) {
          var sx = 0, sy = 0, moved = false;
          btn.addEventListener('pointerdown', function (e) {
            sx = e.clientX; sy = e.clientY; moved = false;
            e.stopPropagation();
          });
          btn.addEventListener('pointermove', function (e) {
            var dx = e.clientX - sx, dy = e.clientY - sy;
            if (dx * dx + dy * dy > 100) moved = true;
            e.stopPropagation();
          });
          btn.addEventListener('pointercancel', function (e) { moved = true; e.stopPropagation(); });
          btn.addEventListener('click', function (e) {
            e.stopPropagation();
            if (moved) return;
            if (Number(btn.dataset.cbStep) > 0) self.incCharm(card);
            else self.decCharm(card);
          });
        });
      });
    }

    async loadCatalog() {
      if (this.root.dataset.charmSource !== 'tags' || this.catalogLoading) return;
      var grid = this.root.querySelector('[data-cb-catalog-grid]');
      var status = this.root.querySelector('[data-cb-catalog-status]');
      var retry = this.root.querySelector('[data-cb-catalog-retry]');
      if (!grid) return;
      var self = this;
      if (retry && !retry.dataset.bound) {
        retry.dataset.bound = 'true';
        retry.addEventListener('click', function () { self.loadCatalog(); });
      }
      var tags = this.filterBtns.map(function (btn) { return btn.dataset.cbFilter; }).filter(function (tag) { return tag && tag !== 'all'; });
      if (!tags.length) {
        if (status) status.textContent = 'Henüz charm bulunmuyor.';
        return;
      }
      this.catalogLoading = true;
      if (retry) retry.hidden = true;
      if (status) { status.hidden = false; status.textContent = 'Charm’lar yükleniyor…'; }
      var url = new URL(this.root.dataset.charmSearchUrl || '/search', location.origin);
      url.searchParams.set('q', '(' + tags.map(function (tag) { return 'tag:"' + tag.replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"'; }).join(' OR ') + ')');
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[unavailable_products]', 'show');
      url.searchParams.set('section_id', 'charm-catalog');
      var searchPath = url.pathname;
      var known = new Set(this.charmCards.map(function (card) { return card.dataset.productId; }));
      var visited = new Set();
      try {
        while (url) {
          if (url.origin !== location.origin || url.pathname !== searchPath || visited.has(url.href)) throw new Error('Invalid charm catalog page');
          visited.add(url.href);
          var response = await fetch(url.href, { credentials: 'same-origin' });
          if (!response.ok) throw new Error('Charm catalog unavailable');
          var doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          var page = doc.querySelector('[data-cb-catalog-page]');
          if (!page) throw new Error('Charm catalog missing');
          if (this.root.isConnected === false) return;
          var added = [];
          page.querySelectorAll('[data-cb-charm]').forEach(function (card) {
            if (!card.dataset.productId || known.has(card.dataset.productId)) return;
            known.add(card.dataset.productId);
            grid.appendChild(card);
            added.push(card);
          });
          this.charmCards = this.charmCards.concat(added);
          this.bindCharmCards(added);
          this.selectFilter(this.state.filter || 'all');
          this.render();
          var next = page.dataset.nextUrl;
          url = next ? new URL(next, url) : null;
          if (url) url.searchParams.set('section_id', 'charm-catalog');
        }
        if (status) {
          status.textContent = this.charmCards.length ? '' : 'Bu etiketlere uygun charm bulunamadı.';
          status.hidden = this.charmCards.length > 0;
        }
      } catch (error) {
        if (status) status.textContent = 'Charm listesinin tamamı yüklenemedi. Lütfen tekrar deneyin.';
        if (retry) retry.hidden = false;
      } finally {
        this.catalogLoading = false;
      }
    }

    normalizeTag(value) {
      return String(value || '').trim().toLocaleLowerCase('tr-TR');
    }

    selectFilter(filter) {
      this.state.filter = filter || 'all';
      var selected = this.normalizeTag(this.state.filter);
      this.filterBtns.forEach(function (btn) {
        var on = (btn.dataset.cbFilter || 'all') === filter;
        btn.classList.toggle('is-selected', on);
        btn.setAttribute('aria-pressed', String(on));
      });
      this.charmCards.forEach(function (card) {
        if (selected === 'all') { card.hidden = false; return; }
        var tags = (card.dataset.cbTags || '').split('||').map(function (tag) {
          return String(tag || '').trim().toLocaleLowerCase('tr-TR');
        });
        card.hidden = tags.indexOf(selected) === -1;
      });
    }

    charmFromCard(card) {
      return {
        variantId: Number(card.dataset.variantId),
        productId: Number(card.dataset.productId),
        title: card.dataset.title || '',
        price: parseInt(card.dataset.price, 10) || 0,
        image: card.dataset.image || '',
        scale: parseFloat(card.dataset.scale) || 1
      };
    }

    /* ============ renk (varyant) ============ */
    selectVariant(btn) {
      if (this.submitting || !btn || btn.disabled || this.variantCount(btn) !== this.state.count) return;
      this.state.baseVariantId = Number(btn.dataset.variantId);
      this.state.basePrice = parseInt(btn.dataset.variantPrice, 10) || 0;
      this.state.baseImage = btn.dataset.variantImage || this.state.baseImage;
      this.state.color = btn.dataset.color;
      this.variantBtns.forEach(function (b) { b.classList.toggle('is-selected', b === btn); b.setAttribute('aria-pressed', String(b === btn)); });
      if (this.els.variantName) this.els.variantName.textContent = btn.dataset.color || '';
      this.setPreviewRatio(btn.dataset.variantAspect);
      this.swapBaseImage(this.state.baseImage, btn.dataset.variantTitle || '');
      this.render();
    }

    setPreviewRatio(ratio) {
      var n = parseFloat(ratio);
      if (!this.els.canvas || !isFinite(n) || n <= 0) return;
      this.root.style.setProperty('--cb-preview-ratio', String(n));
      this.syncImageLayers();
    }

    swapBaseImage(src, alt) {
      var img = this.els.baseImage;
      if (!img || !src) return;
      img.src = src; img.alt = alt || img.alt;
      this.syncImageLayers();
    }

    syncImageLayers() {
      var canvas = this.els.canvas, img = this.els.baseImage;
      if (!canvas || !img || !this.els.pointsLayer || !this.els.overlay) return;
      if (typeof canvas.getBoundingClientRect !== 'function' || typeof img.getBoundingClientRect !== 'function') return;
      var cr = canvas.getBoundingClientRect();
      var ir = img.getBoundingClientRect();
      if (!cr.width || !cr.height || !ir.width || !ir.height) return;
      var left = ir.left - cr.left - (canvas.clientLeft || 0);
      var top = ir.top - cr.top - (canvas.clientTop || 0);
      [this.els.pointsLayer, this.els.overlay].forEach(function (layer) {
        layer.style.left = left + 'px';
        layer.style.top = top + 'px';
        layer.style.width = ir.width + 'px';
        layer.style.height = ir.height + 'px';
      });
    }

    /* ============ yerleştirme ============ */
    placeCharmAtPoint(idx, charm) {
      if (this.submitting || !this.state.baseVariantId || idx == null || !charm || !this.pointsByIndex[idx]) return;
      this.state.placements[idx] = charm;
      this.ensureBuilderId();
      this.clearError();
      this.render({ jostleAt: idx });
    }

    removeCharmAtPoint(idx) {
      if (this.submitting) return;
      if (this.state.placements[idx]) {
        delete this.state.placements[idx];
        this.render();
      }
    }

    nearestPoint(xPct, yPct, onlyEmpty) {
      var best = null, bestD = Infinity;
      for (var i = 0; i < this.points.length; i++) {
        var p = this.points[i];
        if (onlyEmpty && this.state.placements[p.index]) continue;
        var dx = p.x - xPct, dy = p.y - yPct, d = dx * dx + dy * dy;
        if (d < bestD) { bestD = d; best = p; }
      }
      if (best && bestD > SNAP_RADIUS_PCT * SNAP_RADIUS_PCT) return null;
      return best;
    }

    // sürükle-bırak: en yakın BOŞ nokta (mesafe sınırı yok → tuvalin her yerine bırakılabilir)
    nearestFreePoint(xPct, yPct) {
      var best = null, bestD = Infinity;
      for (var i = 0; i < this.points.length; i++) {
        var p = this.points[i];
        if (this.state.placements[p.index]) continue;
        var dx = p.x - xPct, dy = p.y - yPct, d = dx * dx + dy * dy;
        if (d < bestD) { bestD = d; best = p; }
      }
      return best;
    }

    pointerToPercent(clientX, clientY) {
      this.syncImageLayers();
      var target = this.els.baseImage || this.els.canvas;
      if (!target || typeof target.getBoundingClientRect !== 'function') target = this.els.canvas;
      var r = target.getBoundingClientRect();
      return { x: ((clientX - r.left) / r.width) * 100, y: ((clientY - r.top) / r.height) * 100 };
    }

    /* ============ sürükle-bırak (Pointer Events) ============ */
    bindDrag(card) {
      var self = this, ghost = null, dragging = false, pid = null, sx = 0, sy = 0, raf = 0, lx = 0, ly = 0, finished = false;

      function onDown(e) {
        if (self.submitting) return;
        if (e.pointerType === 'touch') return;
        if (e.button != null && e.button > 0) return;
        finished = false;
        pid = e.pointerId; sx = e.clientX; sy = e.clientY;
        try { card.setPointerCapture(pid); } catch (_) {}
        // Capture kartta kalsın AMA document'a da fallback bağla: capture koparsa (mobil/DOM/layout)
        // drop + temizlik yine garanti çalışsın → "tutulu kalma" bug'ı kapanır.
        card.addEventListener('pointermove', onMove);
        card.addEventListener('pointerup', onUp);
        card.addEventListener('pointercancel', onCancel);
        card.addEventListener('lostpointercapture', onLost);
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
        document.addEventListener('pointercancel', onCancel);
      }
      function begin() {
        dragging = true;
        ghost = document.createElement('div');
        ghost.className = 'cb-drag-ghost';
        var im = document.createElement('img'); im.src = card.dataset.image; im.alt = ''; im.draggable = false;
        ghost.appendChild(im); document.body.appendChild(ghost);
        document.body.classList.add('cb-dragging');
      }
      function moveGhost(x, y) {
        lx = x; ly = y;
        if (raf) return;
        raf = requestAnimationFrame(function () {
          raf = 0;
          if (ghost) ghost.style.transform = 'translate(' + lx + 'px,' + ly + 'px) translate(-50%,-50%)';
          var pc = self.pointerToPercent(lx, ly);
          var np = self.nearestFreePoint(pc.x, pc.y);
          self.points.forEach(function (p) { if (p.el) p.el.classList.toggle('is-target', np && p.index === np.index); });
        });
      }
      function onMove(e) {
        if (e.pointerId !== pid) return;
        if (!dragging) {
          var dx = e.clientX - sx, dy = e.clientY - sy;
          if (dx * dx + dy * dy < DRAG_THRESHOLD * DRAG_THRESHOLD) return;
          begin();
        }
        moveGhost(e.clientX, e.clientY);
      }
      function dropAt(x, y) {
        var pc = self.pointerToPercent(x, y);
        var np = self.nearestFreePoint(pc.x, pc.y);
        if (np) self.placeCharmAtPoint(np.index, self.charmFromCard(card));
      }
      function onUp(e) {
        if (e.pointerId !== pid) return;
        if (dragging) dropAt(e.clientX, e.clientY);
        else self.toggleArm(card);  // tık = arm/disarm
        cleanup();
      }
      function onCancel(e) {
        if (e && e.pointerId !== pid) return;
        cleanup();
      }
      function onLost() {
        // Capture beklenmedik koptu: sürükleme sürüyorsa son konuma drop dene, sonra garanti temizlik.
        if (dragging) dropAt(lx, ly);
        cleanup();
      }
      function cleanup() {
        if (finished) return;   // idempotent: onUp/onCancel/lostpointercapture hangisi önce gelirse TEK SEFER
        finished = true;
        dragging = false;
        if (raf) { cancelAnimationFrame(raf); raf = 0; }
        try { card.releasePointerCapture(pid); } catch (_) {}
        if (ghost) { ghost.remove(); ghost = null; }
        document.body.classList.remove('cb-dragging');
        self.points.forEach(function (p) { if (p.el) p.el.classList.remove('is-target'); });
        card.removeEventListener('pointermove', onMove);
        card.removeEventListener('pointerup', onUp);
        card.removeEventListener('pointercancel', onCancel);
        card.removeEventListener('lostpointercapture', onLost);
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        document.removeEventListener('pointercancel', onCancel);
      }
      card.addEventListener('pointerdown', onDown);
      // klavye: Enter = arm
      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); self.toggleArm(card); }
      });
    }

    toggleArm(card) {
      if (this.submitting || !this.state.baseVariantId) return;
      if (this.state.armed === card) { this.disarm(); return; }
      this.disarm();
      this.state.armed = card;
      card.classList.add('is-armed');
      card.setAttribute('aria-pressed', 'true');
      this.root.classList.add('cb-has-armed');
    }
    disarm() {
      if (this.state.armed) {
        this.state.armed.classList.remove('is-armed');
        this.state.armed.setAttribute('aria-pressed', 'false');
      }
      this.state.armed = null;
      this.root.classList.remove('cb-has-armed');
    }

    /* ============ hesaplar ============ */
    placedCount() { return Object.keys(this.state.placements).length; }
    priceTotal() {
      return this.state.basePrice;
    }
    /* ============ stepper (+/-) ============ */
    isFull() { return this.placedCount() >= this.points.length; }
    nextFreePoint() {
      for (var i = 0; i < this.points.length; i++) {
        if (!this.state.placements[this.points[i].index]) return this.points[i];
      }
      return null;
    }
    incCharm(card) {
      if (card.dataset.available === 'false') return;
      var p = this.nextFreePoint();
      if (!p) { this.showError('Tüm yerler dolu. Önce bir charm çıkarın.'); return; }
      this.placeCharmAtPoint(p.index, this.charmFromCard(card));
    }
    decCharm(card) {
      var vid = Number(card.dataset.variantId), pl = this.state.placements, last = null;
      for (var k in pl) {
        if (pl[k].variantId === vid) { var idx = Number(k); if (last === null || idx > last) last = idx; }
      }
      if (last !== null) this.removeCharmAtPoint(last);
    }
    ensureBuilderId() {
      if (!this.state.builderId) this.state.builderId = 'cb-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
    }
    prefersReduced() {
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return true;
      return document.documentElement.classList.contains('prefers-reduced-motion');
    }

    /* ============ render ============ */
    render(opts) {
      this.syncImageLayers();
      this.root.querySelector('[data-cb-color-group]').hidden = false;
      var charmsGroup = this.root.querySelector('[data-cb-charms-group]');
      if (charmsGroup) charmsGroup.hidden = false;
      this.root.querySelector('[data-cb-status]').textContent = !this.state.count
        ? 'Önce charm sayısını seçin.' : !this.state.baseVariantId
        ? 'Bu seçim için satışa uygun ip rengi bulunmuyor.' : this.points.length === 0
        ? 'Bu modelin charm yerleşimi henüz hazır değil.' : '';
      var self = this, pl = this.state.placements;

      // charm kart adet + stepper disable
      var counts = {};
      for (var k in pl) counts[pl[k].variantId] = (counts[pl[k].variantId] || 0) + 1;
      var full = this.isFull(), noPoints = this.points.length === 0;
      this.charmCards.forEach(function (card) {
        var id = Number(card.dataset.variantId), c = counts[id] || 0;
        var unavailable = card.dataset.available === 'false';
        var badge = card.querySelector('[data-cb-charm-count]');
        if (badge) { badge.textContent = c; badge.setAttribute('data-zero', c === 0 ? 'true' : 'false'); }
        var minus = card.querySelector('[data-cb-step="-1"]');
        var plus = card.querySelector('[data-cb-step="1"]');
        if (minus) minus.disabled = unavailable || c === 0;
        if (plus) plus.disabled = unavailable || !self.state.baseVariantId || noPoints || full;
        card.classList.toggle('is-active', c > 0);
      });

      // Point occupancy
      this.points.forEach(function (p) { if (p.el) p.el.classList.toggle('is-occupied', !!pl[p.index]); });

      // boş tuval durumu
      if (this.els.canvas) this.els.canvas.classList.toggle('is-empty', this.placedCount() === 0);

      // özet
      if (this.els.slotsUsed) this.els.slotsUsed.textContent = this.placedCount();
      if (this.els.slotsTotal) this.els.slotsTotal.textContent = this.state.count || 0;
      if (this.els.total) this.els.total.innerHTML = this.state.baseVariantId ? formatMoney(this.priceTotal(), this.moneyFormat) : '—';
      if (this.els.addBtn) this.els.addBtn.disabled = !!this.submitting || !this.state.baseVariantId || noPoints || this.placedCount() !== this.points.length;

      this.reconcileOverlay(opts || {});
    }

    /* ============ overlay reconciler (point anahtarlı) ============ */
    computePlacement() {
      var out = [], desired = new Set(), pl = this.state.placements;
      for (var k in pl) {
        var idx = Number(k), p = this.pointsByIndex[idx];
        if (!p) continue;
        var c = pl[k];
        out.push({ unitId: 'p' + idx, x: p.x, y: p.y, image: c.image, title: c.title, scale: c.scale, variantId: c.variantId });
        desired.add('p' + idx);
      }
      return { out: out, desired: desired };
    }

    reconcileOverlay(opts) {
      var overlay = this.els.overlay;
      if (!overlay) return;
      var reduce = this.prefersReduced(), self = this;
      var res = this.computePlacement(), out = res.out, desired = res.desired;

      this.placed.byId.forEach(function (node, id) {
        if (!desired.has(id)) { self.placed.byId.delete(id); self.leaveNode(node, reduce); }
      });
      for (var i = 0; i < out.length; i++) {
        var pl = out[i], node = this.placed.byId.get(pl.unitId);
        if (!node) { node = this.createNode(pl); this.placed.byId.set(pl.unitId, node); this.enterNode(node, reduce); }
        else if (node.dataset.variant !== String(pl.variantId)) { this.swapNode(node, pl, reduce); }
        else { this.moveNode(node, pl, reduce); }
      }
      if (!reduce && opts && opts.jostleAt != null) this.jostleNear(opts.jostleAt);
    }

    createNode(pl) {
      var wrap = document.createElement('div');
      wrap.className = 'cb-charm';
      wrap.dataset.unitId = pl.unitId;
      wrap.dataset.variant = String(pl.variantId);
      wrap.style.setProperty('--cb-x', pl.x + '%');
      wrap.style.setProperty('--cb-y', pl.y + '%');
      wrap.style.setProperty('--cb-w', (CHARM_FRAC * 100 * (pl.scale || 1)).toFixed(2) + '%');
      this.applySwayVars(wrap, this._swaySeq++);
      var img = document.createElement('img');
      img.className = 'cb-charm__img'; img.src = pl.image; img.alt = pl.title || '';
      img.decoding = 'async'; img.draggable = false;
      wrap.appendChild(img);
      if (!this.editorMode) this.bindNodeInteract(wrap);   // yerleşik charm'ı mouse ile sök / taşı
      this.els.overlay.appendChild(wrap);
      return wrap;
    }
    enterNode(wrap, reduce) {
      if (reduce) { wrap.classList.add('cb-charm--settled'); return; }
      wrap.classList.add('cb-charm--entering');
      var img = wrap.querySelector('.cb-charm__img');
      this.once(img, 'animationend', function () {
        wrap.classList.remove('cb-charm--entering');
        wrap.classList.add('cb-charm--settled');
        img.style.willChange = 'auto';
      });
    }
    leaveNode(wrap, reduce) {
      if (reduce) { wrap.remove(); return; }
      wrap.classList.remove('cb-charm--settled', 'cb-charm--entering', 'cb-charm--jostle');
      wrap.classList.add('cb-charm--leaving');
      var img = wrap.querySelector('.cb-charm__img'), done = false;
      this.once(img, 'animationend', function () { if (done) return; done = true; wrap.remove(); });
    }
    moveNode(wrap, pl, reduce) {
      var cx = wrap.style.getPropertyValue('--cb-x'), cy = wrap.style.getPropertyValue('--cb-y');
      if (cx === pl.x + '%' && cy === pl.y + '%') return;
      wrap.style.setProperty('--cb-x', pl.x + '%');
      wrap.style.setProperty('--cb-y', pl.y + '%');
      if (!reduce) {
        wrap.style.transition = 'left var(--duration-normal,500ms) var(--easing,ease), top var(--duration-normal,500ms) var(--easing,ease)';
        this.once(wrap, 'transitionend', function () { wrap.style.transition = ''; });
      }
    }
    // Aynı point'te charm değişti (dolu point'e bırakma) → görseli/boyutu yenile + drop-in
    swapNode(wrap, pl, reduce) {
      wrap.dataset.variant = String(pl.variantId);
      wrap.style.setProperty('--cb-w', (CHARM_FRAC * 100 * (pl.scale || 1)).toFixed(2) + '%');
      var img = wrap.querySelector('.cb-charm__img');
      img.src = pl.image; img.alt = pl.title || '';
      if (reduce) return;
      wrap.classList.remove('cb-charm--settled');
      wrap.classList.add('cb-charm--entering');
      this.once(img, 'animationend', function () {
        wrap.classList.remove('cb-charm--entering');
        wrap.classList.add('cb-charm--settled');
      });
    }
    applySwayVars(wrap, i) {
      var dur = 6.5 + ((i * 0.37) % 2.5), phase = -(((i * 0.53) % 1)) * dur, amp = 1.2 + ((i * 0.4) % 1.0);
      var yaw = 5 + ((i * 0.6) % 5);   // 3D çevirme açısı — her charm biraz farklı (canlı/organik)
      wrap.style.setProperty('--sway-dur', dur.toFixed(2) + 's');
      wrap.style.setProperty('--sway-delay', phase.toFixed(2) + 's');
      wrap.style.setProperty('--sway-amp', amp.toFixed(2) + 'deg');
      wrap.style.setProperty('--sway-yaw', yaw.toFixed(2) + 'deg');
    }

    /* ============ yerleşik charm: mouse ile sök / taşı (Pointer Events) ============
       Tık → o charm'ı çıkar.  Sürükle → en yakın BOŞ noktaya taşı; tuval DIŞINA bırak → çıkar. */
    bindNodeInteract(wrap) {
      var self = this, pid = null, sx = 0, sy = 0, raf = 0, lx = 0, ly = 0;
      var dragging = false, finished = false, idx = -1;

      function inside(pc) { return pc.x >= 0 && pc.x <= 100 && pc.y >= 0 && pc.y <= 100; }

      function onDown(e) {
        if (self.submitting) return;
        if (e.pointerType === 'touch') return;
        if (e.button != null && e.button > 0) return;        // yalnız sol tık
        idx = Number(wrap.dataset.unitId.slice(1));
        if (self.state.placements[idx] == null) return;
        pid = e.pointerId; sx = e.clientX; sy = e.clientY;
        dragging = false; finished = false;
        e.preventDefault(); e.stopPropagation();             // canvas/point click'lerine sızma
        try { wrap.setPointerCapture(pid); } catch (_) {}
        wrap.addEventListener('pointermove', onMove);
        wrap.addEventListener('pointerup', onUp);
        wrap.addEventListener('pointercancel', onCancel);
        wrap.addEventListener('lostpointercapture', onLost);
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
      }
      function begin() {
        dragging = true;
        wrap.classList.remove('cb-charm--settled', 'cb-charm--entering');
        wrap.classList.add('cb-charm--dragging');
        document.body.classList.add('cb-dragging');          // boş noktalar görünür olsun
      }
      function moveTo(x, y) {
        lx = x; ly = y;
        if (raf) return;
        raf = requestAnimationFrame(function () {
          raf = 0;
          var pc = self.pointerToPercent(lx, ly), ins = inside(pc);
          wrap.style.setProperty('--cb-x', pc.x.toFixed(2) + '%');
          wrap.style.setProperty('--cb-y', pc.y.toFixed(2) + '%');
          var np = ins ? self.nearestFreePoint(pc.x, pc.y) : null;
          self.points.forEach(function (p) { if (p.el) p.el.classList.toggle('is-target', !!np && p.index === np.index); });
          wrap.classList.toggle('cb-charm--will-remove', !ins);
        });
      }
      function onMove(e) {
        if (e.pointerId !== pid) return;
        if (!dragging) {
          var dx = e.clientX - sx, dy = e.clientY - sy;
          if (dx * dx + dy * dy < DRAG_THRESHOLD * DRAG_THRESHOLD) return;
          begin();
        }
        moveTo(e.clientX, e.clientY);
      }
      function onUp(e) {
        if (e.pointerId !== pid) return;
        if (!dragging) {
          self.removeCharmAtPoint(idx);                                  // tık → sök
        } else {
          var pc = self.pointerToPercent(e.clientX, e.clientY), ins = inside(pc);
          var np = ins ? self.nearestFreePoint(pc.x, pc.y) : null;
          var charm = self.state.placements[idx];
          if (!ins) {
            self.removeCharmAtPoint(idx);                                // tuval dışı → sök
          } else if (np && np.index !== idx && charm) {
            self.removeCharmAtPoint(idx); self.placeCharmAtPoint(np.index, charm);   // boş noktaya taşı
          } else {
            self.render();                                               // yerine yumuşak geri otur
          }
        }
        cleanup();
      }
      function onCancel(e) { if (e && e.pointerId !== pid) return; if (dragging) self.render(); cleanup(); }
      function onLost() { if (dragging) self.render(); cleanup(); }
      function cleanup() {
        if (finished) return;                                // idempotent: up/cancel/lost TEK sefer temizler
        finished = true; dragging = false;
        if (raf) { cancelAnimationFrame(raf); raf = 0; }
        try { wrap.releasePointerCapture(pid); } catch (_) {}
        document.body.classList.remove('cb-dragging');
        wrap.classList.remove('cb-charm--dragging', 'cb-charm--will-remove');
        if (!wrap.classList.contains('cb-charm--leaving')) wrap.classList.add('cb-charm--settled');  // sallanmayı geri getir
        self.points.forEach(function (p) { if (p.el) p.el.classList.remove('is-target'); });
        wrap.removeEventListener('pointermove', onMove);
        wrap.removeEventListener('pointerup', onUp);
        wrap.removeEventListener('pointercancel', onCancel);
        wrap.removeEventListener('lostpointercapture', onLost);
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
      }
      wrap.addEventListener('pointerdown', onDown);
    }
    jostleNear(idx) {
      var origin = this.pointsByIndex[idx]; if (!origin) return;
      var self = this;
      this.placed.byId.forEach(function (node, id) {
        if (id === 'p' + idx) return;
        var oi = Number(id.slice(1)), p = self.pointsByIndex[oi]; if (!p) return;
        var dx = p.x - origin.x, dy = p.y - origin.y;
        if (dx * dx + dy * dy < 18 * 18 && !node.classList.contains('cb-charm--entering')) {
          node.style.setProperty('--jostle-amp', '2deg');
          self.playOneShot(node, 'cb-charm--jostle');
        }
      });
    }

    /* ============ reset ============ */
    reset() {
      if (this.submitting) return;
      this.state.placements = {};
      this.state.builderId = null;
      this.disarm();
      this.clearError();
      this.render();
    }

    /* ============ composite + sepet ============ */
    buildComposite() {
      var self = this, S = COMPOSITE_SIZE;
      var canvas = document.createElement('canvas'); canvas.width = S; canvas.height = S;
      var ctx = canvas.getContext('2d');
      var pl = this.state.placements;

      return loadImage(setWidth(this.state.baseImage, S)).then(function (baseImg) {
        // base = kare varsayımı; contain ile çiz (kare ise tam kaplar)
        var bs = Math.min(S / baseImg.naturalWidth, S / baseImg.naturalHeight);
        var bw = baseImg.naturalWidth * bs, bh = baseImg.naturalHeight * bs;
        ctx.drawImage(baseImg, (S - bw) / 2, (S - bh) / 2, bw, bh);

        var jobs = [];
        for (var k in pl) {
          (function (idx) {
            var c = pl[idx], p = self.pointsByIndex[idx]; if (!p) return;
            jobs.push(loadImage(setWidth(c.image, 800)).then(function (ci) {
              var w = S * CHARM_FRAC * (c.scale || 1);
              var h = w * (ci.naturalHeight / ci.naturalWidth);
              var x = (p.x / 100) * S - w / 2, y = (p.y / 100) * S;
              ctx.drawImage(ci, x, y, w, h);
            }).catch(function () { /* görsel yüklenemezse atla */ }));
          })(k);
        }
        return Promise.all(jobs);
      }).then(function () {
        return new Promise(function (resolve) { canvas.toBlob(function (b) { resolve(b); }, 'image/png'); });
      });
    }

    charmSummaryText() {
      var counts = {}, order = [], pl = this.state.placements;
      for (var k in pl) {
        var c = pl[k];
        if (!counts[c.variantId]) { counts[c.variantId] = { title: c.title, n: 0 }; order.push(c.variantId); }
        counts[c.variantId].n++;
      }
      return order.map(function (id) { var e = counts[id]; return e.title + (e.n > 1 ? ' x' + e.n : ''); }).join(', ');
    }

    selectedCharmData() {
      var items = [], pl = this.state.placements;
      for (var i = 0; i < this.points.length; i++) {
        var point = this.points[i];
        var charm = pl[point.index];
        if (!charm) continue;
        items.push({
          point: point.index,
          variant_id: charm.variantId,
          product_id: charm.productId,
          title: charm.title
        });
      }
      return {
        items: items,
        variantIds: items.map(function (item) { return item.variant_id; }),
        productIds: items.map(function (item) { return item.product_id; })
      };
    }

    addToCart() {
      if (this.submitting) return;
      if (!this.state.baseVariantId || this.points.length === 0 || this.placedCount() !== this.points.length) {
        this.showError('Lütfen tüm charm alanlarını tamamlayın.');
        return;
      }
      var self = this;
      if (this.els.summary) this.els.summary.classList.add('is-loading');
      this.clearError();
      this.ensureBuilderId();

      this.submitting = true;
      this.root.classList.add('is-submitting');
      this.els.addBtn.disabled = true;
      this.buildComposite().then(function (blob) {
        // Fixed price: add only the base product; keep charms as line properties.
        var selectedCharms = self.selectedCharmData();
        var fd = new FormData();
        fd.append('id', String(self.state.baseVariantId));
        fd.append('quantity', '1');
        fd.append('properties[_builder_id]', self.state.builderId);
        fd.append('properties[_charm_variant_ids]', selectedCharms.variantIds.join(','));
        fd.append('properties[_charm_product_ids]', selectedCharms.productIds.join(','));
        fd.append('properties[_charm_selection_json]', JSON.stringify(selectedCharms.items));
        fd.append('properties[Secili charmlar]', self.charmSummaryText());
        fd.append('properties[Charm adedi]', String(self.state.count));
        fd.append('properties[İp rengi]', self.state.color);
        if (blob) fd.append('properties[Tasarim Gorseli]', blob, 'tasarim.png');
        return fetch(self.urls.add, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, // Content-Type YOK (boundary)
          body: fd
        });
      }).then(function (res) {
        return res.json().then(function (d) { if (!res.ok) throw d; return d; });
      }).then(function () {
        self.submitting = false;
        self.onAdded();
      }).catch(function (err) {
        self.showError((err && err.description) || 'Sepete eklenirken bir hata oluştu.');
      }).finally(function () {
        self.submitting = false;
        self.root.classList.remove('is-submitting');
        self.render();
        if (self.els.summary) self.els.summary.classList.remove('is-loading');
      });
    }

    onAdded() {
      this.reset();
      var drawer = document.querySelector('cart-drawer-modern');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        if (typeof drawer.refresh === 'function') drawer.refresh();
      } else {
        document.dispatchEvent(new CustomEvent('cart:item-added', { bubbles: true }));
        window.location.href = this.urls.base;
      }
    }

    /* ============ yardımcı ============ */
    once(el, type, fn) {
      var h = function (e) { if (e.target !== el) return; el.removeEventListener(type, h); fn(e); };
      el.addEventListener(type, h);
    }
    playOneShot(el, cls) {
      el.classList.remove(cls); void el.offsetWidth; el.classList.add(cls);
      this.once(el, 'animationend', function () { el.classList.remove(cls); });
    }
    showError(msg) { if (this.els.error) { this.els.error.textContent = msg; this.els.error.hidden = false; } }
    clearError() { if (this.els.error) { this.els.error.textContent = ''; this.els.error.hidden = true; } }

    /* ============ Point Editörü (design_mode) ============ */
    initEditor() {
      var self = this;
      // Canlı sayfada (?cb-points) editör div'i hidden gelir → göster
      var editorEl = this.root.querySelector('[data-cb-editor]');
      if (editorEl) editorEl.hidden = false;
      var out = this.root.querySelector('[data-cb-editor-out]');
      var img = this.els.baseImage, layer = this.els.pointsLayer;
      if (!img || !layer) return;
      var pts = this.points.map(function (p) { return { x: p.x, y: p.y }; });

      function renderDots() {
        layer.innerHTML = '';
        pts.forEach(function (p, i) {
          var d = document.createElement('button');
          d.type = 'button'; d.className = 'charm-builder__point is-editor';
          d.style.left = p.x + '%'; d.style.top = p.y + '%';
          d.textContent = (i + 1);
          d.title = 'Sürükle: taşı · Tıkla: sil';
          // sil
          d.addEventListener('click', function (e) { e.stopPropagation(); if (d.dataset.moved === 'true') { d.dataset.moved = 'false'; return; } pts.splice(i, 1); renderDots(); output(); });
          // sürükle-nudge
          d.addEventListener('pointerdown', function (e) {
            e.stopPropagation(); var pid = e.pointerId, moved = false;
            try { d.setPointerCapture(pid); } catch (_) {}
            function mv(ev) {
              moved = true; d.dataset.moved = 'true'; var pc = self.pointerToPercent(ev.clientX, ev.clientY);
              p.x = Math.min(100, Math.max(0, +pc.x.toFixed(2))); p.y = Math.min(100, Math.max(0, +pc.y.toFixed(2)));
              d.style.left = p.x + '%'; d.style.top = p.y + '%';
            }
            function up() { d.removeEventListener('pointermove', mv); d.removeEventListener('pointerup', up); if (moved) output(); }
            d.addEventListener('pointermove', mv); d.addEventListener('pointerup', up);
          });
          layer.appendChild(d);
        });
      }
      function output() {
        if (self.state.count) self.layouts[self.state.count] = pts.map(function (p) { return { x: +p.x.toFixed(2), y: +p.y.toFixed(2) }; });
        if (out) out.value = JSON.stringify(self.layouts);
      }
      this.editorLoad = function () {
        pts = (self.layouts[self.state.count] || self.points).map(function (p) { return { x: p.x, y: p.y }; });
        renderDots(); output();
      };

      // görsele tıkla = nokta ekle
      this.els.canvas.addEventListener('click', function (e) {
        if (!self.state.count || pts.length >= self.state.count) return;
        if (e.target.closest('.charm-builder__point')) return; // dot'a tık silme içindir
        var pc = self.pointerToPercent(e.clientX, e.clientY);
        pts.push({ x: +pc.x.toFixed(2), y: +pc.y.toFixed(2) });
        renderDots(); output();
      });

      var loadBtn = this.root.querySelector('[data-cb-editor-load]');
      var undoBtn = this.root.querySelector('[data-cb-editor-undo]');
      var clearBtn = this.root.querySelector('[data-cb-editor-clear]');
      if (loadBtn) loadBtn.addEventListener('click', function () { pts = self.points.map(function (p) { return { x: p.x, y: p.y }; }); renderDots(); output(); });
      if (undoBtn) undoBtn.addEventListener('click', function () { pts.pop(); renderDots(); output(); });
      if (clearBtn) clearBtn.addEventListener('click', function () { pts = []; renderDots(); output(); });

      renderDots(); output();
    }
  }

  function initAll() {
    var roots = document.querySelectorAll('[data-charm-builder]');
    for (var i = 0; i < roots.length; i++) new CharmBuilder(roots[i]);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target && e.target.querySelector ? e.target.querySelector('[data-charm-builder]') : null;
    if (root) new CharmBuilder(root);
  });
})();
